import { useState, useCallback, ReactNode } from 'react';
import { Icon } from '../icon';
import Icons from '@/const/icons';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ZooContainerProps {
	children: ReactNode;
	initialZoom?: number;
	minZoom?: number;
	maxZoom?: number;
	step?: number;
	height?: string;
	className?: string;
	scrollRef?: React.RefObject<HTMLDivElement>;
	/**
	 * When true (default), the content pane scrolls with a always-visible
	 * Radix scrollbar (retainer documents). When false, overflow is hidden
	 * (image preview zoom).
	 */
	scrollable?: boolean;
	/** Show zoom bar on all breakpoints (default: only `lg+`). */
	alwaysShowControls?: boolean;
	/** Center the control bar horizontally at the bottom of the image. */
	centerControls?: boolean;
	/** Optional trailing control (e.g. external link) inside the zoom bar. */
	controlsEndSlot?: ReactNode;
}

export const ZoomControls = ({
	children,
	initialZoom = 1,
	minZoom = 0.5,
	maxZoom = 2,
	step = 0.1,
	height = '73vh',
	className = '',
	scrollRef,
	scrollable = true,
	alwaysShowControls = false,
	centerControls = false,
	controlsEndSlot,
}: ZooContainerProps) => {
	const [zoom, setZoom] = useState(initialZoom);

	const zoomIn = useCallback(() => {
		setZoom((prev) => Math.min(prev + step, maxZoom));
	}, [step, maxZoom]);

	const zoomOut = useCallback(() => {
		setZoom((prev) => Math.max(prev - step, minZoom));
	}, [step, minZoom]);

	const handleZoomChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setZoom(parseFloat(e.target.value));
		},
		[]
	);

	const controlsVisibility = alwaysShowControls ? 'flex' : 'hidden lg:flex';
	const controlsPosition = centerControls
		? 'left-1/2 -translate-x-1/2'
		: 'right-[40%]';
	const controlsWidth = controlsEndSlot
		? 'w-auto max-w-none min-w-[192px]'
		: 'max-w-[192px] w-[192px]';

	const scaledContent = (
		<div
			className={
				scrollable
					? 'w-full min-h-full transition-transform duration-150'
					: 'h-full w-full transition-transform duration-150'
			}
			style={{
				transform: `scale(${zoom})`,
				transformOrigin: scrollable ? 'top center' : 'center center',
			}}
		>
			{children}
		</div>
	);

	return (
		<>
			<style jsx>{`
				input[type='range']::-webkit-slider-thumb {
					-webkit-appearance: none;
					appearance: none;
					width: 12px;
					height: 12px;
					background: #8039df;
					border-color: #8039df;
					border-radius: 50%;
					cursor: pointer;
				}
				input[type='range']::-moz-range-thumb {
					width: 16px;
					height: 16px;
					background: #8039df;
					border-radius: 50%;
					border: none;
					cursor: pointer;
				}
			`}</style>

			{/* Fixed viewport — zoom must not change this box size */}
			<div
				className={`border border-[#E8E8E8] rounded-2xl overflow-hidden relative ${className}`}
				style={{ height }}
			>
				<div
					className={`${controlsVisibility} absolute bottom-3 ${controlsPosition} max-h-10 z-10 gap-2 rounded-3xl p-2 shadow-shadow2 bg-gray-5 items-center ${controlsWidth}`}
				>
					<button
						type="button"
						onClick={zoomOut}
						className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded"
					>
						<Icon src={Icons.searchZoomOut} width={25} height={25} />
					</button>
					<input
						type="range"
						min={minZoom}
						max={maxZoom}
						step={step}
						value={zoom}
						onChange={handleZoomChange}
						className="w-24 cursor-pointer"
						style={{
							WebkitAppearance: 'none',
							appearance: 'none',
							background: '#E5E7EB',
							height: '4px',
							borderRadius: '9999px',
						}}
					/>
					<button
						type="button"
						onClick={zoomIn}
						className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded"
					>
						<Icon src={Icons.searchZoomIn} width={25} height={25} />
					</button>
					{controlsEndSlot ? (
						<>
							<span className="w-px h-4 bg-grey-100 shrink-0" aria-hidden />
							{controlsEndSlot}
						</>
					) : null}
				</div>

				{scrollable ? (
					/*
					  Native overlay scrollbars fade on scroll — Radix type="always"
					  paints a custom thumb that stays visible for scroll-to-end UX.
					*/
					<ScrollArea
						type="always"
						className="h-full w-full bg-white"
						viewportRef={scrollRef}
						viewportClassName="!pr-5 lg:!p-8 lg:!pr-6 lg:!pb-16"
						scrollbarClassName="!h-auto top-3 bottom-3 right-2 w-2 rounded-full"
					>
						{scaledContent}
					</ScrollArea>
				) : (
					<div
						ref={scrollRef}
						className="h-full w-full overflow-hidden bg-white lg:p-8 lg:pb-16"
					>
						{scaledContent}
					</div>
				)}
			</div>
		</>
	);
};
